import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { VagaItem } from './VagaItem';
import { formatDate, parseBackendDate } from '../../utils/formatters';
import { styles, colors } from '../../styles/globalStyles';

export const GradeSemanal = React.memo(({
  grade,
  user,
  onAgendar,
  onDesmarcar,
  onSolicitarWaitlist,
  onSairDaWaitlist,
  isAdminView = false,
  onEditarVaga,
  onTrancarVaga,
  onDestrancarVaga,
  onLiberarVaga,
  naFilaMap = {},
  minhasFilas = [],
  userVagasNoDia = [],
  onRefresh,
  visualizacaoConfig = null,
  createApi = null,
}) => {
  const [diaAberto, setDiaAberto] = useState(null);
  const [horarioAberto, setHorarioAberto] = useState(null);
  const [diasExpandidos, setDiasExpandidos] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [filaSlotKey, setFilaSlotKey] = useState(null);
  const [filaSlotData, setFilaSlotData] = useState({});

  // Detectar se está em mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // FUNÇÃO PARA FILTRAR VAGAS PASSADAS
  const filtrarVagasPassadas = useCallback((vagas) => {
    if (!vagas || vagas.length === 0) return vagas;

    const janelaDia = visualizacaoConfig?.janela_dia ?? 1;
    const exibirOcupadas = visualizacaoConfig?.exibir_vagas_ocupadas ?? true;

    if (janelaDia >= 0) {
      // Backend já filtrou pelo janela_dia — frontend só aplica a regra de vagas ocupadas
      if (exibirOcupadas) return vagas;
      return vagas.filter(v => !v.agendamento_id || v.aluno_google_id === user?.googleId);
    }

    // janelaDia < 0: bloquear vagas que começam nas próximas |janelaDia|h
    const horasAntecedencia = Math.abs(janelaDia);
    const tempoLimite = new Date(Date.now() + horasAntecedencia * 60 * 60 * 1000);

    return vagas.filter(vaga => {
      const dataVaga = parseBackendDate(vaga.data_hora);
      if (!dataVaga) return false;
      if (!exibirOcupadas && vaga.agendamento_id && vaga.aluno_google_id !== user?.googleId) return false;
      return dataVaga >= tempoLimite;
    });
  }, [visualizacaoConfig, user?.googleId]);

  // Calcular totais gerais com filtro aplicado (admin não filtra)
  const totaisGerais = useMemo(() => {
    const gradeFiltrada = isAdminView ? grade : filtrarVagasPassadas(grade);
    const agora = new Date();
    const total     = gradeFiltrada?.length || 0;
    const fixas     = gradeFiltrada?.filter(v => v.aluno_fixo_id).length || 0;
    const trancadas = gradeFiltrada?.filter(v => v.trancada && !v.aluno_fixo_id).length || 0;
    const ocupadas  = gradeFiltrada?.filter(v => v.agendamento_id && !v.trancada).length || 0;
    const livres    = gradeFiltrada?.filter(v => !v.aluno_fixo_id && !v.trancada && !v.agendamento_id && !v.rascunho).length || 0;

    return { total, fixas, trancadas, ocupadas, livres };
  }, [grade, filtrarVagasPassadas, isAdminView]);

  const { dias, horarios, vagasPorDia, vagasPorDiaEHorario } = useMemo(() => {
    // Se for admin view, não filtrar vagas passadas
    const gradeFiltrada = isAdminView ? grade : filtrarVagasPassadas(grade);
    
    if (!gradeFiltrada || gradeFiltrada.length === 0) {
      return { 
        dias: [], 
        horarios: [],
        vagasPorDia: {},
        vagasPorDiaEHorario: {}
      };
    }
    
    // Determinar a data de referência
    let dataReferencia;
    
    if (gradeFiltrada.length > 0 && gradeFiltrada[0].data_hora) {
      dataReferencia = parseBackendDate(gradeFiltrada[0].data_hora);
    } else {
      dataReferencia = new Date();
    }
    
    // Ajustar para início da semana (segunda-feira)
    const diaSemana = dataReferencia.getDay();
    const diff = diaSemana === 0 ? -6 : 1 - diaSemana;
    
    const inicioSemana = new Date(dataReferencia);
    inicioSemana.setDate(dataReferencia.getDate() + diff);
    inicioSemana.setHours(0, 0, 0, 0);
    
    // CORREÇÃO AQUI: Criar array de 7 dias (segunda a domingo)
    const diasArray = Array.from({ length: 7 }, (_, i) => {
      const data = new Date(inicioSemana);
      data.setDate(inicioSemana.getDate() + i);
      const diaStr = data.toISOString().split('T')[0];
      const diaIndex = data.getDay();
      
      // Nomes completos e abreviados dos dias
      const diasCompletos = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];
      const diasAbreviados = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
      
      return {
        date: data,
        diaSemana: diasCompletos[diaIndex],
        diaSemanaAbrev: diasAbreviados[diaIndex],
        dataFormatada: formatDate(data),
        diaStr,
        diaNumero: diaIndex, // 0=domingo, 1=segunda, ..., 6=sábado
        vagas: [],
        vagasPorHorario: new Map(),
        isExpanded: false
      };
    });
    
    // Organizar vagas por dia e por horário
    gradeFiltrada.forEach(vaga => {
      const dataVaga = parseBackendDate(vaga.data_hora);
      if (!dataVaga) return;
      
      const dataVagaStr = dataVaga.toISOString().split('T')[0];
      const horaFormatada = dataVaga.toLocaleTimeString('pt-BR', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
      
      const diaIndex = diasArray.findIndex(dia => dia.diaStr === dataVagaStr);
      
      if (diaIndex !== -1) {
        const vagaComHorario = { 
          ...vaga, 
          hora: dataVaga.getHours(),
          minuto: dataVaga.getMinutes(),
          horaFormatada,
          diaSemana: diasArray[diaIndex].diaSemana,
          diaSemanaAbrev: diasArray[diaIndex].diaSemanaAbrev,
          diaIndex: diaIndex
        };
        
        diasArray[diaIndex].vagas.push(vagaComHorario);
        
        // Agrupar por horário
        if (!diasArray[diaIndex].vagasPorHorario.has(horaFormatada)) {
          diasArray[diaIndex].vagasPorHorario.set(horaFormatada, []);
        }
        diasArray[diaIndex].vagasPorHorario.get(horaFormatada).push(vagaComHorario);
      }
    });
    
    // Ordenar vagas por horário e depois por id dentro do mesmo slot
    diasArray.forEach(dia => {
      dia.vagas.sort((a, b) => {
        const diff = (a.hora * 60 + a.minuto) - (b.hora * 60 + b.minuto);
        return diff !== 0 ? diff : (parseInt(a.id) || 0) - (parseInt(b.id) || 0);
      });
    });
    
    // Extrair horários únicos ordenados
    const todosHorarios = gradeFiltrada.map(v => {
      const d = parseBackendDate(v.data_hora);
      if (!d) return null;
      const hora = d.getHours();
      const minuto = d.getMinutes();
      return hora * 100 + minuto;
    }).filter(Boolean);
    
    const horariosUnicos = [...new Set(todosHorarios)].sort((a, b) => a - b);
    
    const horariosArray = horariosUnicos.map(horaInt => {
      const horas = Math.floor(horaInt / 100);
      const minutos = horaInt % 100;
      const horaStr = `${horas.toString().padStart(2, '0')}:${minutos.toString().padStart(2, '0')}`;
      
      // Calcular período do dia
      let periodo = '';
      if (horas < 12) periodo = 'Manhã';
      else if (horas < 18) periodo = 'Tarde';
      else periodo = 'Noite';
      
      return {
        hora: horaStr,
        horas,
        minutos,
        periodo,
        id: horaStr.replace(':', '')
      };
    });
    
    // Criar objeto com vagas por dia
    const vagasPorDiaObj = {};
    diasArray.forEach(dia => {
      vagasPorDiaObj[dia.diaSemana] = {
        total: dia.vagas.length,
        fixas:     dia.vagas.filter(v => v.aluno_fixo_id).length,
        trancadas: dia.vagas.filter(v => v.trancada && !v.aluno_fixo_id).length,
        livres:    dia.vagas.filter(v => !v.aluno_fixo_id && !v.trancada && !v.agendamento_id && !v.rascunho).length,
        todasVagas: dia.vagas
      };
    });
    
    // Criar objeto com vagas por dia e horário
    const vagasPorDiaEHorarioObj = {};
    diasArray.forEach(dia => {
      vagasPorDiaEHorarioObj[dia.diaSemana] = {};
      horariosArray.forEach(horario => {
        const vagas = dia.vagasPorHorario.get(horario.hora) || [];
        vagasPorDiaEHorarioObj[dia.diaSemana][horario.id] = {
          total: vagas.length,
          livres: vagas.filter(v => !v.aluno_fixo_id && !v.trancada && !v.agendamento_id && !v.rascunho).length,
          vagas: vagas
        };
      });
    });
    
    return {
      dias: isAdminView ? diasArray : diasArray.filter(d => d.vagas.length > 0),
      horarios: horariosArray,
      vagasPorDia: vagasPorDiaObj,
      vagasPorDiaEHorario: vagasPorDiaEHorarioObj
    };
  }, [grade, isAdminView, filtrarVagasPassadas]);

  const handleVerFila = useCallback((key, vagaId) => {
    if (filaSlotKey === key) { setFilaSlotKey(null); return; }
    setFilaSlotKey(key);
    if (!createApi || !vagaId) return;
    setFilaSlotData(prev => ({ ...prev, [key]: { loading: true, fila: prev[key]?.fila || [] } }));
    const api = createApi();
    api.get(`/admin/waitlist/${vagaId}`)
      .then(res => setFilaSlotData(prev => ({ ...prev, [key]: { loading: false, fila: res.data.fila || [] } })))
      .catch(() => setFilaSlotData(prev => ({ ...prev, [key]: { loading: false, fila: [] } })));
  }, [filaSlotKey, createApi]);

  const handleRemoverDaFilaSlot = useCallback(async (key, vagaId, waitlistId) => {
    if (!createApi) return;
    if (!window.confirm('Remover este aluno da fila de espera?')) return;
    const api = createApi();
    try {
      await api.delete(`/admin/waitlist/${waitlistId}`);
      setFilaSlotData(prev => ({ ...prev, [key]: { loading: true, fila: prev[key]?.fila || [] } }));
      const res = await api.get(`/admin/waitlist/${vagaId}`);
      setFilaSlotData(prev => ({ ...prev, [key]: { loading: false, fila: res.data.fila || [] } }));
      if (onRefresh) onRefresh();
    } catch (err) {
      alert('Erro ao remover: ' + (err.response?.data?.error || err.message));
      setFilaSlotData(prev => ({ ...prev, [key]: { ...prev[key], loading: false } }));
    }
  }, [createApi, onRefresh]);

  const handleToggleDia = useCallback((diaSemana) => {
    if (isMobile) {
      setDiaAberto(diaAberto === diaSemana ? null : diaSemana);
      setHorarioAberto(null);
    } else {
      setDiasExpandidos(prev => 
        prev.includes(diaSemana) 
          ? prev.filter(d => d !== diaSemana)
          : [...prev, diaSemana]
      );
    }
  }, [diaAberto, isMobile]);

  const handleToggleHorario = useCallback((horarioId, diaSemana) => {
    if (isMobile) {
      setHorarioAberto(horarioAberto === horarioId ? null : horarioId);
    } else {
      // No desktop, alterna o horário
      setHorarioAberto(horarioAberto === horarioId ? null : horarioId);
    }
  }, [horarioAberto, isMobile]);

  const handleRefresh = () => {
    if (onRefresh) onRefresh();
    else window.location.reload();
  };

  // Função para obter horários disponíveis para um dia específico
  const getHorariosParaDia = (diaSemana) => {
    const dia = dias.find(d => d.diaSemana === diaSemana);
    if (!dia) return [];

    return horarios.filter(horario => {
      const vagas = dia.vagasPorHorario.get(horario.hora) || [];
      return vagas.length > 0;
    }).map(horario => {
      const vagas = dia.vagasPorHorario.get(horario.hora) || [];
      const disponiveis = vagas.filter(v => !v.agendamento_id && !v.trancada && !v.rascunho && !v.aluno_fixo_id).length;
      // fila_count é igual para todas as vagas do mesmo data_hora — pega do primeiro
      const filaCount = Number(vagas[0]?.fila_count) || 0;

      return {
        ...horario,
        totalVagas: vagas.length,
        disponiveis: disponiveis,
        filaCount,
      };
    });
  };

  // Total de pessoas em fila num dia (soma de fila_count por slot único)
  const getFilaCountDia = (diaSemana) => {
    const dia = dias.find(d => d.diaSemana === diaSemana);
    if (!dia) return 0;
    let total = 0;
    dia.vagasPorHorario.forEach(vagas => {
      total += Number(vagas[0]?.fila_count) || 0;
    });
    return total;
  };

  // Agora vem as condicionais de renderização
  if (!grade) {
    return (
      <div style={{
        ...styles.card,
        textAlign: 'center',
        padding: isMobile ? '1.5rem' : '3rem'
      }}>
        <div style={{
          width: isMobile ? '24px' : '48px',
          height: isMobile ? '24px' : '48px',
          border: '3px solid #e2e8f0',
          borderTopColor: colors.primary,
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: isMobile ? '0 auto 1rem' : '0 auto 1.5rem'
        }}></div>
        <h3 style={{ 
          marginBottom: isMobile ? '0.5rem' : '0.5rem',
          fontSize: isMobile ? '1.25rem' : '1.5rem'
        }}>Carregando grade...</h3>
        <p style={{ 
          color: colors.gray, 
          marginBottom: isMobile ? '1rem' : '1.5rem',
          fontSize: isMobile ? '0.875rem' : '1rem'
        }}>
          Aguarde enquanto buscamos os horários disponíveis
        </p>
      </div>
    );
  }

  // Verifica se há vagas após o filtro (para não-admin)
  const gradeFiltrada = isAdminView ? grade : filtrarVagasPassadas(grade);
  if (gradeFiltrada && gradeFiltrada.length === 0) {
    return (
      <div style={{
        ...styles.card,
        textAlign: 'center',
        padding: isMobile ? '1.5rem' : '3rem'
      }}>
        <div style={{
          width: isMobile ? '40px' : '60px',
          height: isMobile ? '40px' : '60px',
          backgroundColor: colors.grayLight,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: isMobile ? '0 auto 1rem' : '0 auto 1.5rem',
          fontSize: isMobile ? '1.25rem' : '1.5rem'
        }}>
          📅
        </div>
        <h3 style={{
          marginBottom: isMobile ? '0.5rem' : '0.5rem',
          fontSize: isMobile ? '1.25rem' : '1.5rem'
        }}>
          {isAdminView ? 'Nenhuma vaga disponível' : 'Nenhuma aula disponível nesta semana'}
        </h3>
        <p style={{
          color: colors.gray,
          marginBottom: isMobile ? '1rem' : '1.5rem',
          fontSize: isMobile ? '0.875rem' : '1rem'
        }}>
          {isAdminView
            ? 'Não há vagas cadastradas para esta semana. Use as Ações Rápidas para gerar e publicar.'
            : 'As aulas desta semana ainda não foram publicadas ou não há vagas para os próximos dias. Tente a semana seguinte ou entre em contato com o estúdio.'
          }
        </p>
        <button
          onClick={handleRefresh}
          style={styles.button}
        >
          ↻ Atualizar
        </button>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f8fafc',
      padding: isMobile ? '10px' : '20px',
      maxWidth: '100%',
      overflowX: 'hidden'
    }}>
      {/* Header Principal */}
      <div style={{
        background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
        color: colors.white,
        padding: isMobile ? '16px 12px' : '24px 20px',
        borderRadius: isMobile ? '12px' : '16px',
        marginBottom: isMobile ? '16px' : '24px',
        boxShadow: '0 8px 32px rgba(102, 126, 234, 0.4)',
        maxWidth: '100%',
        overflow: 'hidden'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'flex-start' : 'center',
          marginBottom: isMobile ? '16px' : '24px',
          gap: isMobile ? '12px' : '20px',
          maxWidth: '100%'
        }}>
          <div style={{
            maxWidth: isMobile ? '100%' : '50%',
            overflow: 'hidden'
          }}>
            <h1 style={{
              margin: 0,
              fontSize: isMobile ? '1.5rem' : '28px',
              fontWeight: '700',
              marginBottom: isMobile ? '8px' : '8px',
              lineHeight: 1.2,
              wordWrap: 'break-word'
            }}>
              Agenda Semanal {isAdminView && '- Modo Admin'}
            </h1>
            <p style={{
              margin: 0,
              opacity: 0.9,
              fontSize: isMobile ? '0.875rem' : '16px',
              wordWrap: 'break-word'
            }}>
              {/* CORREÇÃO AQUI: Mostrar de domingo a sábado */}
              {dias.length > 0 && `${formatDate(dias[0].date)} - ${formatDate(dias[dias.length - 1].date)}`}
              {!isAdminView && ' • Vagas passadas filtradas'}
            </p>
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : 'repeat(auto-fit, minmax(80px, 1fr))',
            gap: isMobile ? '8px' : '12px',
            width: isMobile ? '100%' : 'auto',
            maxWidth: '100%'
          }}>
            <div style={{ 
              textAlign: 'center',
              padding: isMobile ? '8px' : '10px',
              backgroundColor: 'rgba(255,255,255,0.15)',
              borderRadius: isMobile ? '8px' : '8px',
              minWidth: isMobile ? '0' : '80px'
            }}>
              <div style={{ 
                fontSize: isMobile ? '1.25rem' : '20px',
                fontWeight: '700',
                lineHeight: 1.2
              }}>
                {totaisGerais.total}
              </div>
              <div style={{ 
                fontSize: isMobile ? '0.75rem' : '13px',
                opacity: 0.9,
                marginTop: isMobile ? '2px' : '2px'
              }}>
                Total
              </div>
            </div>
            <div style={{ 
              textAlign: 'center',
              padding: isMobile ? '8px' : '10px',
              backgroundColor: 'rgba(255,255,255,0.15)',
              borderRadius: isMobile ? '8px' : '8px',
              minWidth: isMobile ? '0' : '80px'
            }}>
              <div style={{
                fontSize: isMobile ? '1.25rem' : '20px',
                fontWeight: '700',
                color: '#fef3c7',
                lineHeight: 1.2
              }}>
                {totaisGerais.fixas}
              </div>
              <div style={{
                fontSize: isMobile ? '0.75rem' : '13px',
                opacity: 0.9,
                marginTop: isMobile ? '2px' : '2px'
              }}>
                Fixas
              </div>
            </div>
            <div style={{
              textAlign: 'center',
              padding: isMobile ? '8px' : '10px',
              backgroundColor: 'rgba(255,255,255,0.15)',
              borderRadius: isMobile ? '8px' : '8px',
              minWidth: isMobile ? '0' : '80px'
            }}>
              <div style={{
                fontSize: isMobile ? '1.25rem' : '20px',
                fontWeight: '700',
                color: '#fee2e2',
                lineHeight: 1.2
              }}>
                {totaisGerais.trancadas}
              </div>
              <div style={{
                fontSize: isMobile ? '0.75rem' : '13px',
                opacity: 0.9,
                marginTop: isMobile ? '2px' : '2px'
              }}>
                Trancadas
              </div>
            </div>
            <div style={{
              textAlign: 'center',
              padding: isMobile ? '8px' : '10px',
              backgroundColor: 'rgba(255,255,255,0.15)',
              borderRadius: isMobile ? '8px' : '8px',
              minWidth: isMobile ? '0' : '80px'
            }}>
              <div style={{
                fontSize: isMobile ? '1.25rem' : '20px',
                fontWeight: '700',
                color: '#fca5a5',
                lineHeight: 1.2
              }}>
                {totaisGerais.ocupadas}
              </div>
              <div style={{
                fontSize: isMobile ? '0.75rem' : '13px',
                opacity: 0.9,
                marginTop: isMobile ? '2px' : '2px'
              }}>
                Ocupadas
              </div>
            </div>
            <div style={{
              textAlign: 'center',
              padding: isMobile ? '8px' : '10px',
              backgroundColor: 'rgba(255,255,255,0.15)',
              borderRadius: isMobile ? '8px' : '8px',
              minWidth: isMobile ? '0' : '80px'
            }}>
              <div style={{
                fontSize: isMobile ? '1.25rem' : '20px',
                fontWeight: '700',
                color: '#d1fae5',
                lineHeight: 1.2
              }}>
                {totaisGerais.livres}
              </div>
              <div style={{
                fontSize: isMobile ? '0.75rem' : '13px',
                opacity: 0.9,
                marginTop: isMobile ? '2px' : '2px'
              }}>
                Livres
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lista de Dias - CORREÇÃO: Agora mostra 7 dias */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: isMobile ? '8px' : '14px',
        marginBottom: isMobile ? '16px' : '20px',
        maxWidth: '100%'
      }}>
        {dias.map((dia) => {
          const stats = vagasPorDia[dia.diaSemana] || { total: 0, livres: 0 };
          const isDiaAberto = isMobile ? diaAberto === dia.diaSemana : diasExpandidos.includes(dia.diaSemana);
          const horariosDoDia = getHorariosParaDia(dia.diaSemana);
          const filaDia = isAdminView ? getFilaCountDia(dia.diaSemana) : 0;
          
          return (
            <div
              key={dia.diaSemana}
              style={{
                backgroundColor: colors.white,
                borderRadius: isMobile ? '12px' : '14px',
                overflow: 'hidden',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                maxWidth: '100%'
              }}
            >
              {/* Cabeçalho do Dia */}
              <div
                onClick={() => handleToggleDia(dia.diaSemana)}
                style={{
                  padding: isMobile ? '14px' : '18px',
                  borderBottom: isDiaAberto ? `2px solid ${colors.primary}` : 'none',
                  backgroundColor: isDiaAberto ? colors.primaryLight : colors.white,
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  transition: 'all 0.3s ease',
                  maxWidth: '100%'
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: isMobile ? '12px' : '12px',
                  minWidth: 0,
                  flex: 1
                }}>
                  <div style={{
                    width: isMobile ? '36px' : '36px',
                    height: isMobile ? '36px' : '36px',
                    backgroundColor: isDiaAberto ? colors.primary : colors.grayLight,
                    borderRadius: isMobile ? '8px' : '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: '700',
                    color: isDiaAberto ? colors.white : colors.dark,
                    fontSize: isMobile ? '14px' : '14px',
                    flexShrink: 0
                  }}>
                    {dia.diaSemanaAbrev.charAt(0)}
                  </div>
                  <div style={{
                    minWidth: 0,
                    flex: 1
                  }}>
                    <div style={{
                      fontSize: isMobile ? '1rem' : '18px',
                      fontWeight: '700',
                      color: colors.dark,
                      marginBottom: isMobile ? '4px' : '4px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {isMobile ? dia.diaSemanaAbrev : dia.diaSemana}
                    </div>
                    <div style={{
                      fontSize: isMobile ? '0.875rem' : '14px',
                      color: colors.gray,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {dia.dataFormatada} • {stats.total} vagas
                    </div>
                  </div>
                </div>
                
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: isMobile ? '8px' : '8px',
                  flexShrink: 0
                }}>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    gap: '4px'
                  }}>
                    <div style={{
                      fontSize: isMobile ? '0.875rem' : '14px',
                      fontWeight: '600',
                      color: colors.success
                    }}>
                      {stats.livres} livres
                    </div>
                    {isAdminView && filaDia > 0 && (
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '3px',
                        backgroundColor: '#f5f3ff',
                        border: '1px solid #c4b5fd',
                        borderRadius: '10px',
                        padding: '2px 7px',
                        fontSize: '11px',
                        fontWeight: '700',
                        color: '#7c3aed',
                        whiteSpace: 'nowrap'
                      }}>
                        ⏳ {filaDia} na fila
                      </div>
                    )}
                    {!isMobile && !isAdminView && (
                      <div style={{
                        fontSize: '12px',
                        color: colors.gray
                      }}>
                        {horariosDoDia.length} horários
                      </div>
                    )}
                  </div>
                  
                  <div style={{
                    width: isMobile ? '20px' : '20px',
                    height: isMobile ? '20px' : '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transform: isDiaAberto ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s ease',
                    color: colors.primary,
                    flexShrink: 0,
                    fontSize: isMobile ? '14px' : '14px'
                  }}>
                    ▼
                  </div>
                </div>
              </div>

              {/* Conteúdo do Dia (Horários) */}
              {isDiaAberto && (
                <div style={{
                  padding: isMobile ? '12px' : '16px',
                  backgroundColor: '#f8fafc',
                  maxWidth: '100%'
                }}>
                  {horariosDoDia.length > 0 ? (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: isMobile ? '8px' : '10px'
                    }}>
                      {horariosDoDia.map((horario) => {
                        const isHorarioAberto = horarioAberto === horario.id;
                        const vagasDoHorario = dia.vagasPorHorario.get(horario.hora) || [];
                        const slotKey = `${dia.diaSemana}-${horario.id}`;
                        const isFilaAberta = filaSlotKey === slotKey;
                        const dadosFila = filaSlotData[slotKey];
                        const primeiraVagaId = vagasDoHorario[0]?.id;

                        return (
                          <div
                            key={horario.id}
                            style={{
                              backgroundColor: colors.white,
                              borderRadius: isMobile ? '10px' : '10px',
                              overflow: 'hidden',
                              boxShadow: isFilaAberta ? '0 4px 16px rgba(139,92,246,0.15)' : '0 2px 6px rgba(0,0,0,0.05)',
                              maxWidth: '100%',
                              border: isFilaAberta ? '1.5px solid #ddd6fe' : '1.5px solid transparent',
                              transition: 'box-shadow 0.2s, border-color 0.2s'
                            }}
                          >
                            {/* Cabeçalho do Horário */}
                            <div
                              onClick={() => handleToggleHorario(horario.id, dia.diaSemana)}
                              style={{
                                padding: isMobile ? '14px 16px' : '14px 18px',
                                backgroundColor: isHorarioAberto ? '#f0f9ff' : colors.white,
                                cursor: 'pointer',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                transition: 'all 0.3s ease',
                                maxWidth: '100%'
                              }}
                            >
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: isMobile ? '12px' : '12px',
                                minWidth: 0,
                                flex: 1
                              }}>
                                <div style={{
                                  width: isMobile ? '44px' : '44px',
                                  height: isMobile ? '44px' : '44px',
                                  backgroundColor: isHorarioAberto ? colors.primary : '#f0f9ff',
                                  borderRadius: isMobile ? '8px' : '8px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontWeight: '700',
                                  color: isHorarioAberto ? colors.white : colors.primary,
                                  fontSize: isMobile ? '16px' : '16px',
                                  lineHeight: 1,
                                  flexShrink: 0
                                }}>
                                  <span>{horario.hora.split(':')[0]}</span>
                                  <span style={{ fontSize: isMobile ? '11px' : '11px' }}>h</span>
                                </div>
                                <div style={{
                                  minWidth: 0,
                                  flex: 1
                                }}>
                                  <div style={{
                                    fontSize: isMobile ? '1rem' : '18px',
                                    fontWeight: '700',
                                    color: colors.dark,
                                    marginBottom: isMobile ? '2px' : '2px'
                                  }}>
                                    {horario.hora}
                                  </div>
                                  <div style={{
                                    fontSize: isMobile ? '0.875rem' : '14px',
                                    color: colors.gray
                                  }}>
                                    {horario.periodo} • {vagasDoHorario.length} vagas
                                  </div>
                                </div>
                              </div>
                              
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: isMobile ? '10px' : '10px',
                                flexShrink: 0
                              }}>
                                <div style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'flex-end',
                                  gap: '4px'
                                }}>
                                  <div style={{
                                    fontSize: isMobile ? '0.875rem' : '14px',
                                    fontWeight: '600',
                                    color: horario.livres > 0 ? colors.success : colors.error
                                  }}>
                                    {horario.livres} livres
                                  </div>
                                  {isAdminView && horario.filaCount > 0 && (
                                    <button
                                      onClick={e => { e.stopPropagation(); handleVerFila(slotKey, primeiraVagaId); }}
                                      style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        backgroundColor: isFilaAberta ? '#7c3aed' : '#f5f3ff',
                                        border: `1.5px solid ${isFilaAberta ? '#7c3aed' : '#c4b5fd'}`,
                                        borderRadius: '10px',
                                        padding: '4px 10px',
                                        fontSize: '11px',
                                        fontWeight: '700',
                                        color: isFilaAberta ? 'white' : '#7c3aed',
                                        whiteSpace: 'nowrap',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s'
                                      }}
                                    >
                                      ⏳ {horario.filaCount} na fila
                                      <span style={{ fontSize: '10px', opacity: 0.8 }}>{isFilaAberta ? '▲' : '▼'}</span>
                                    </button>
                                  )}
                                  {!isMobile && !isAdminView && (
                                    <div style={{
                                      fontSize: '11px',
                                      color: colors.gray
                                    }}>
                                      Clique para ver
                                    </div>
                                  )}
                                </div>
                                
                                <div style={{
                                  width: isMobile ? '20px' : '20px',
                                  height: isMobile ? '20px' : '20px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transform: isHorarioAberto ? 'rotate(180deg)' : 'rotate(0deg)',
                                  transition: 'transform 0.3s ease',
                                  color: colors.primary,
                                  flexShrink: 0,
                                  fontSize: isMobile ? '14px' : '14px'
                                }}>
                                  ▼
                                </div>
                              </div>
                            </div>

                            {/* Painel de fila de espera do slot (admin) */}
                            {isAdminView && isFilaAberta && (
                              <div style={{
                                borderTop: '2px solid #ddd6fe',
                                backgroundColor: '#faf5ff',
                                padding: isMobile ? '12px' : '14px 18px',
                                animation: 'fadeIn 0.2s ease'
                              }}>
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  marginBottom: '10px'
                                }}>
                                  <div style={{ fontSize: '12px', fontWeight: '700', color: '#7c3aed' }}>
                                    ⏳ Fila de espera — {horario.hora}
                                  </div>
                                  {dadosFila?.loading && (
                                    <div style={{ fontSize: '11px', color: '#8b5cf6' }}>Carregando...</div>
                                  )}
                                </div>

                                {dadosFila?.loading ? (
                                  <div style={{
                                    height: '40px',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                                  }}>
                                    <div style={{
                                      width: 18, height: 18,
                                      border: '2px solid #ddd6fe', borderTopColor: '#8b5cf6',
                                      borderRadius: '50%', animation: 'spin 1s linear infinite'
                                    }} />
                                  </div>
                                ) : dadosFila?.fila?.length > 0 ? (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    {dadosFila.fila.map((entry, i) => (
                                      <div
                                        key={entry.id}
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '10px',
                                          backgroundColor: 'white',
                                          borderRadius: '8px',
                                          padding: '8px 12px',
                                          border: '1px solid #ede9fe'
                                        }}
                                      >
                                        <div style={{
                                          width: 24, height: 24, flexShrink: 0,
                                          backgroundColor: '#8b5cf6', color: 'white',
                                          borderRadius: '50%', display: 'flex',
                                          alignItems: 'center', justifyContent: 'center',
                                          fontSize: '11px', fontWeight: '700'
                                        }}>
                                          {i + 1}
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                          <div style={{
                                            fontSize: '13px', fontWeight: '600', color: '#1e293b',
                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                                          }}>
                                            {entry.nome}
                                          </div>
                                          <div style={{
                                            fontSize: '11px', color: '#64748b',
                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                                          }}>
                                            {entry.email}
                                          </div>
                                        </div>
                                        <div style={{ fontSize: '11px', color: '#94a3b8', flexShrink: 0 }}>
                                          {new Date(entry.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                                        </div>
                                        <button
                                          onClick={() => handleRemoverDaFilaSlot(slotKey, primeiraVagaId, entry.id)}
                                          title="Remover da fila"
                                          style={{
                                            background: 'none', border: '1px solid #fca5a5',
                                            borderRadius: '6px', cursor: 'pointer',
                                            color: '#ef4444', fontSize: '13px',
                                            width: 26, height: 26, display: 'flex',
                                            alignItems: 'center', justifyContent: 'center',
                                            flexShrink: 0, padding: 0
                                          }}
                                        >
                                          ✕
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                ) : dadosFila && !dadosFila.loading ? (
                                  <div style={{ fontSize: '12px', color: '#94a3b8', textAlign: 'center', padding: '8px' }}>
                                    Fila vazia para este horário
                                  </div>
                                ) : null}
                              </div>
                            )}

                            {/* Vagas do Horário - Layout otimizado */}
                            {isHorarioAberto && (
                              <div style={{
                                padding: isMobile ? '12px' : '16px',
                                backgroundColor: '#f8fafc',
                                borderTop: `1px solid ${colors.grayLight}`,
                                maxWidth: '100%'
                              }}>
                                {vagasDoHorario.length > 0 ? (
                                  <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))',
                                    gap: isMobile ? '12px' : '16px',
                                    maxWidth: '100%'
                                  }}>
                                    {vagasDoHorario.map((vaga, index) => {
                                      const naFilaDireta = naFilaMap[vaga.id]?.naFila === true;
                                      // Detecta fila pelo slot inteiro (data_hora), não só pela vaga exata
                                      const filaDoSlot = !naFilaDireta
                                        ? minhasFilas.find(f => f.data_hora === vaga.data_hora)
                                        : null;
                                      const naFila = naFilaDireta || filaDoSlot != null;
                                      const posicaoNaFila = naFilaMap[vaga.id]?.posicao || filaDoSlot?.posicao || 0;
                                      // Garante que "Sair da fila" usa o vaga_id correto da entrada na fila
                                      const sairDaFilaHandler = filaDoSlot
                                        ? (_id) => onSairDaWaitlist(filaDoSlot.vaga_id)
                                        : onSairDaWaitlist;

                                      return (
                                        <div
                                          key={vaga.id || index}
                                          style={{
                                            animation: `fadeIn 0.3s ease ${index * 0.1}s both`,
                                            maxWidth: '100%'
                                          }}
                                        >
                                          <VagaItem
                                            vaga={vaga}
                                            user={user}
                                            onAgendar={onAgendar}
                                            onDesmarcar={onDesmarcar}
                                            onSolicitarWaitlist={onSolicitarWaitlist}
                                            onSairDaWaitlist={sairDaFilaHandler}
                                            isAdminView={isAdminView}
                                            onEditarVaga={onEditarVaga}
                                            onTrancarVaga={onTrancarVaga}
                                            onDestrancarVaga={onDestrancarVaga}
                                            onLiberarVaga={onLiberarVaga}
                                            naFila={naFila}
                                            posicaoNaFila={posicaoNaFila}
                                            userVagasNoDia={userVagasNoDia}
                                            showDayInfo={false}
                                            showTimeInfo={false}
                                            isCompactView={isMobile}
                                            createApi={isAdminView ? createApi : null}
                                            style={{
                                              maxWidth: '100%',
                                              width: '100%',
                                              padding: isMobile ? '12px' : '16px',
                                              fontSize: isMobile ? '14px' : '14px'
                                            }}
                                          />
                                        </div>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <div style={{
                                    textAlign: 'center',
                                    padding: isMobile ? '30px 20px' : '30px 20px'
                                  }}>
                                    <div style={{
                                      width: isMobile ? '50px' : '50px',
                                      height: isMobile ? '50px' : '50px',
                                      backgroundColor: colors.grayLight,
                                      borderRadius: '50%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      margin: isMobile ? '0 auto 12px' : '0 auto 12px',
                                      fontSize: isMobile ? '1.25rem' : '1.25rem'
                                    }}>
                                      ⏰
                                    </div>
                                    <h3 style={{
                                      margin: '0 0 8px 0',
                                      fontSize: isMobile ? '1rem' : '16px',
                                      fontWeight: '600',
                                      color: colors.dark
                                    }}>
                                      Nenhuma vaga neste horário
                                    </h3>
                                    <p style={{
                                      margin: 0,
                                      fontSize: isMobile ? '0.875rem' : '14px',
                                      color: colors.gray
                                    }}>
                                      Não há vagas disponíveis para {dia.diaSemana} às {horario.hora}
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{
                      textAlign: 'center',
                      padding: isMobile ? '30px 20px' : '30px 20px'
                    }}>
                      <div style={{
                        width: isMobile ? '50px' : '50px',
                        height: isMobile ? '50px' : '50px',
                        backgroundColor: colors.grayLight,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: isMobile ? '0 auto 12px' : '0 auto 12px',
                        fontSize: isMobile ? '1.25rem' : '1.25rem'
                      }}>
                        📅
                      </div>
                      <h3 style={{
                        margin: '0 0 8px 0',
                        fontSize: isMobile ? '1rem' : '16px',
                        fontWeight: '600',
                        color: colors.dark
                      }}>
                        Nenhum horário disponível
                      </h3>
                      <p style={{
                        margin: 0,
                        fontSize: isMobile ? '0.875rem' : '14px',
                        color: colors.gray
                      }}>
                        Não há vagas disponíveis para {dia.diaSemana}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Botão de Atualizar */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        padding: isMobile ? '16px' : '16px'
      }}>
        <button
          onClick={handleRefresh}
          style={{
            ...styles.button,
            padding: isMobile ? '12px 24px' : '12px 24px',
            borderRadius: isMobile ? '12px' : '12px',
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '8px' : '8px',
            fontWeight: '600',
            fontSize: isMobile ? '16px' : '16px',
            maxWidth: '100%'
          }}
        >
          <span style={{ fontSize: isMobile ? '16px' : '16px' }}>↻</span>
          Atualizar Grade
        </button>
      </div>

      {/* CSS Animations */}
      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          /* Estilos responsivos adicionais */
          @media (min-width: 1024px) {
            /* Para telas grandes, ajusta o grid para mostrar 4 colunas */
            .vagas-grid {
              grid-template-columns: repeat(4, 1fr) !important;
            }
          }
          
          @media (max-width: 1023px) and (min-width: 769px) {
            /* Para tablets, mostra 2 colunas */
            .vagas-grid {
              grid-template-columns: repeat(2, 1fr) !important;
            }
          }
          
          /* Ajustes específicos para mobile */
          @media (max-width: 768px) {
            /* Reduzir ainda mais os textos muito longos */
            .mobile-text-truncate {
              white-space: nowrap;
              overflow: hidden;
              textOverflow: ellipsis;
              max-width: 100%;
            }
            
            /* Garantir que as vagas não ultrapassem a largura */
            .vaga-item-mobile {
              transform: scale(0.85);
              transform-origin: top left;
              margin-left: -8px;
              margin-right: -8px;
            }
          }
        `}
      </style>
    </div>
  );
});
